import { useContext, useState } from 'react'
import { TextField, Button, Typography } from '@mui/material'
import PasswordStrengthBar from 'react-password-strength-bar'
import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { updateUserPassword } from '/providers/auth-api'
import StyledPaper from '../paper.jsx'

const PasswordAdmin = () => {
  const { apiSource, apiKey, processing = false, setProcessing } = useContext(AppContext)

  const [stage, setStage] = useState('form')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordScore, setPasswordScore] = useState(0)
  const [passwordError, setPasswordError] = useState('')
  const [confirmError, setConfirmError] = useState('')
  const [submitError, setSubmitError] = useState('')

  const passwordValidationMessage = (value, { requireStrength = false, score } = {}) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return 'Password is required.'
    }
    if (trimmed.length < 8) {
      return 'Password must be at least 8 characters.'
    }
    const effectiveScore = typeof score === 'number' ? score : passwordScore
    if (requireStrength && effectiveScore < 3) {
      return 'Choose a stronger password before continuing.'
    }
    return ''
  }

  const confirmPasswordValidationMessage = (value, comparison = password) => {
    const trimmed = value.trim()
    if (!trimmed) {
      return 'Please confirm your new password.'
    }
    if (trimmed !== comparison.trim()) {
      return 'Passwords do not match.'
    }
    return ''
  }

  const validatePasswordField = (value = password, options = {}) => {
    const message = passwordValidationMessage(value, options)
    setPasswordError(message)
    return !message
  }

  const validateConfirmField = (value = confirmPassword, comparison = password) => {
    const message = confirmPasswordValidationMessage(value, comparison)
    setConfirmError(message)
    return !message
  }

  const handlePasswordScoreChange = (score) => {
    setPasswordScore(score)
    if (passwordError) {
      validatePasswordField(password, { requireStrength: true, score })
    }
    if (confirmError) {
      validateConfirmField(confirmPassword, password)
    }
  }

  const handlePasswordChange = (value) => {
    setPassword(value)
    if (passwordError) {
      validatePasswordField(value, { requireStrength: true })
    }
    if (confirmError) {
      validateConfirmField(confirmPassword, value)
    }
  }

  const handleConfirmChange = (value) => {
    setConfirmPassword(value)
    if (confirmError) {
      validateConfirmField(value)
    }
  }

  const clearForm = () => {
    setPassword('')
    setConfirmPassword('')
    setPasswordScore(0)
    setPasswordError('')
    setConfirmError('')
    setSubmitError('')
  }

  const handleSubmit = async () => {
    const passwordIsValid = validatePasswordField(password, { requireStrength: true, score: passwordScore })
    const confirmIsValid = validateConfirmField(confirmPassword, password)
    if (!passwordIsValid || !confirmIsValid) return

    if (setProcessing) setProcessing(true)
    setSubmitError('')

    try {
      const result = await updateUserPassword({ apiSource, apiKey, password })
      if (result?.error) {
        const message = typeof result.error === 'string' ? result.error : 'Unable to update your password.'
        setSubmitError(message)
        enqueueSnackbar({ message, variant: 'error' })
        return
      }

      enqueueSnackbar({ message: 'Password updated', variant: 'success' })
      setStage('confirmed')
      clearForm()
    } catch (err) {
      const message = err?.response?.data?.error ?? 'Unable to update your password.'
      setSubmitError(message)
      enqueueSnackbar({ message, variant: 'error' })
    } finally {
      if (setProcessing) setProcessing(false)
    }
  }

  if (stage === 'confirmed') {
    return (
      <div className='flex flex-col h-full overflow-y-auto items-center pb-20'>
        <div className='flex flex-col gap-5 max-w-xl w-full mt-20 border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg p-10 text-center'>
          <Typography variant='h4'>Password updated</Typography>
          <Typography>Your password has been changed successfully.</Typography>
          <div className='flex flex-row justify-center gap-5 mt-5'>
            <Button variant='contained' onClick={() => setStage('form')}>Change password again</Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col overflow-y-auto items-center justify-center p-20'>
      <StyledPaper>
        <div className='flex flex-col gap-5 p-5'>
          <Typography variant='h4'>Change your password</Typography>

          <TextField
            label='New password'
            type='password'
            value={password}
            onChange={(e) => handlePasswordChange(e.target.value)}
            onBlur={() => validatePasswordField(password, { requireStrength: true, score: passwordScore })}
            fullWidth
            size='small'
            error={Boolean(passwordError)}
            helperText={passwordError}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <div className='opacity-60 text-sm'>Password strength (good or strong required)</div>
          <PasswordStrengthBar password={password} onChangeScore={handlePasswordScoreChange} />

          <TextField
            label='Confirm password'
            type='password'
            value={confirmPassword}
            onChange={(e) => handleConfirmChange(e.target.value)}
            onBlur={() => validateConfirmField(confirmPassword, password)}
            fullWidth
            size='small'
            error={Boolean(confirmError)}
            helperText={confirmError}
            slotProps={{ inputLabel: { shrink: true } }}
          />

          {submitError ? (
            <Typography color='error' variant='body2'>{submitError}</Typography>
          ) : null}

          <div className='flex flex-row justify-end'>
            <Button
              variant='contained'
              onClick={handleSubmit}
              disabled={processing}
            >
              Update password
            </Button>
          </div>
        </div>
      </StyledPaper>
    </div>
  )
}

export default PasswordAdmin
